import { faBox, faCalendar, faCar, faCut, faTag, faTruck, faUser } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useReception } from '../../hooks/useReception';
import { useReceptionDestinationRefs } from '../../hooks/useReceptionDestinationRefs';
import {
  Package,
  Reception,
  canIdentifyPackage,
  canSplitPackage,
  getDestinationUrl,
  getPackageDestinationReference,
} from '../../models/Reception';
import { PackageStatusBadge } from '../display/PackageStatusBadge';
import { ReceptionStatusBadge } from '../display/ReceptionStatusBadge';
import { LoadingState } from '../feedback/LoadingState';

interface ViewReceptionModalProps {
  receptionId: number;
  onIdentifyPackage?: (reception: Reception, pkg: Package) => void;
  onSplitPackage?: (reception: Reception, pkg: Package) => void;
}

const ViewReceptionModal = forwardRef<ModalCenterRef, ViewReceptionModalProps>(
  ({ receptionId, onIdentifyPackage, onSplitPackage }, ref) => {
    const { t } = useTranslation('receptionsModule');

    // Cargar datos de la recepción
    const { reception, isLoading } = useReception(receptionId);

    const { refByPackageId } = useReceptionDestinationRefs(reception);

    const handleClose = () => {
      (ref as React.RefObject<ModalCenterRef>)?.current?.closeModal();
    };

    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const getPackageTypeLabel = (pkg: Package) => {
      if (pkg.package_type === 'OTHER' && pkg.package_type_other) {
        return pkg.package_type_other;
      }
      return t(`package_types.${pkg.package_type.toLowerCase()}`);
    };

    if (isLoading) {
      return (
        <ModalCenter ref={ref} title={t('modal.view.title')} type="default" width="xl">
          <LoadingState className="h-32" />
        </ModalCenter>
      );
    }

    if (!reception) {
      return (
        <ModalCenter ref={ref} title={t('modal.view.title')} type="default" width="xl">
          <div className="text-center py-8 text-muted-foreground">{t('errors.reception_not_found')}</div>
        </ModalCenter>
      );
    }

    return (
      <ModalCenter
        ref={ref}
        title={
          <div className="flex items-center gap-3">
            <span>{reception.code}</span>
            <ReceptionStatusBadge status={reception.status} />
          </div>
        }
        type="default"
        width="xl"
        className="h-[calc(100%-30px)] sm:h-auto max-h-[90vh]"
      >
        <div className="space-y-6 flex flex-col h-full overflow-hidden">
          {/* Información de la recepción */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">{t('modal.view.reception_info')}</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faTruck} className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">{t('table.carrier')}</p>
                  <p className="text-sm font-medium text-foreground">{reception.carrier}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faCar} className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">{t('table.vehicle_plate')}</p>
                  <p className="text-sm font-medium text-foreground">{reception.vehicle_plate}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faUser} className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">{t('table.driver_name')}</p>
                  <p className="text-sm font-medium text-foreground">{reception.driver_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faCalendar} className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">{t('table.created_at')}</p>
                  <p className="text-sm font-medium text-foreground">{formatDate(reception.created_at)}</p>
                </div>
              </div>
            </div>

            {/* Resumen de paquetes */}
            <div className="flex gap-6 text-sm">
              <div>
                <span className="text-muted-foreground">Total: </span>
                <span className="font-medium text-foreground">{reception.total_qty}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Pendientes: </span>
                <span className="font-medium text-warning">{reception.pending_qty}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Identificados: </span>
                <span className="font-medium text-success">{reception.identified_qty}</span>
              </div>
            </div>
          </div>

          {/* Lista de paquetes */}
          <div className="flex-1 overflow-auto space-y-3">
            <h4 className="text-sm font-medium text-foreground">{t('modal.view.packages_list')}</h4>

            {reception.packages && reception.packages.length > 0 ? (
              <div className="space-y-3 max-h-[300px] overflow-auto pr-2">
                {reception.packages
                  .filter((pkg) => pkg && pkg.id)
                  .map((pkg) => {
                    const destinationRef = refByPackageId.get(pkg.id) ?? getPackageDestinationReference(pkg);
                    return (
                      <div
                        key={pkg.id}
                        className={`p-4 border rounded-lg ${
                          pkg.status === 'IDENTIFIED' ? 'border-success/50 bg-success/5' : 'border-border bg-card'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                              <FontAwesomeIcon icon={faBox} className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <p className="text-sm font-medium text-foreground">{getPackageTypeLabel(pkg)}</p>
                          </div>
                          <PackageStatusBadge status={pkg.status} />
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                          <div>
                            <span className="text-muted-foreground">{t('modal.view.current_quantity')}: </span>
                            <span className="font-medium text-foreground">{pkg.quantity}</span>
                          </div>
                          {pkg.incident_type && (
                            <div>
                              <span className="text-muted-foreground">{t('modal.view.incident')}: </span>
                              <span className="font-medium text-destructive">{pkg.incident_type}</span>
                            </div>
                          )}
                        </div>

                        {/* Info de destino si está identificado */}
                        {pkg.status === 'IDENTIFIED' && pkg.destination_type && (
                          <div className="p-2 bg-success/10 rounded text-sm mb-3">
                            {(() => {
                              const destinationUrl = getDestinationUrl(pkg.destination_type, pkg.destination_id);
                              const badgeLabel =
                                destinationRef?.trim() || t(`destination_types.${pkg.destination_type.toLowerCase()}`);
                              return destinationUrl ? (
                                <Link
                                  to={destinationUrl}
                                  title={badgeLabel}
                                  className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-primary text-white dark:bg-primary dark:text-white hover:bg-primary/90 dark:hover:bg-primary/90 h-8 px-3 max-w-full min-w-0 truncate"
                                >
                                  {badgeLabel}
                                </Link>
                              ) : (
                                <span className="font-medium text-foreground truncate max-w-full" title={badgeLabel}>
                                  {badgeLabel}
                                </span>
                              );
                            })()}
                            {pkg.payload && (
                              <div className="mt-1 text-xs text-muted-foreground">{JSON.stringify(pkg.payload)}</div>
                            )}
                          </div>
                        )}

                        {/* Acciones del paquete */}
                        {reception.status !== 'CLOSED' && pkg.status === 'RECEIVED' && (
                          <div className="flex gap-2 pt-2 border-t border-border">
                            {canIdentifyPackage(pkg, reception.status) && onIdentifyPackage && (
                              <Button variant="outline" size="sm" onClick={() => onIdentifyPackage(reception, pkg)}>
                                <FontAwesomeIcon icon={faTag} className="mr-2 h-3 w-3" />
                                {t('button.identify')}
                              </Button>
                            )}
                            {canSplitPackage(pkg) && onSplitPackage && (
                              <Button variant="outline" size="sm" onClick={() => onSplitPackage(reception, pkg)}>
                                <FontAwesomeIcon icon={faCut} className="mr-2 h-3 w-3" />
                                {t('button.split')}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">{t('modal.view.no_packages')}</div>
            )}
          </div>

          {/* Botón cerrar */}
          <div className="flex justify-end pt-4 border-t border-border">
            <Button variant="outline" onClick={handleClose} size="lg">
              {t('button.close')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

ViewReceptionModal.displayName = 'ViewReceptionModal';

export default ViewReceptionModal;
